import { useEffect, useCallback, useState, useRef } from 'react'
import useListActions from './useLineActions'
import useListBulkActions from './useListBulkActions'
import useSelectionState from '../../hooks/selection/useSelectionState'
import useSelectionResetEffects from '../../hooks/selection/useSelectionResetEffects'
import useBulkSelectionActions from '../../hooks/selection/useBulkSelectionActions'
import useFormModal from '../modal/useModalDialog'
import { useModalStack } from '../../context/modalStackContext'
import { PopUp } from '../modal/PopUp'
import useListExport from './useListExport'
import ExportModal from './ExportModal'
import useListImport from './useListImport'
import ImportModal from './ImportModal'
import removeElementsByKey from '../../utils/removeElementsbyKey'

const useViewTable = ({ config, endpoint, table, result, debouncedSearchTerm, filterTerm, serverFilter, entity, order, selectAction, setOrder, onRefreshAggregate }) => {
  const [modalType, setModalType] = useState(null) // 'line' or 'bulk'

  const { handleAction, handleActions, moveItem, moveLoading, confirmMove, clearMoveItem } = useListActions(config, endpoint, setModalType, onRefreshAggregate)

  const getFilterActions = (actionType, currentFilter) => {
    const topLevelActions = config[actionType]
    const filterLevelActions = currentFilter?.[actionType]

    if (!topLevelActions && !filterLevelActions) return null

    const mergedMenuItems = [
      ...(topLevelActions?.menuItems || []),
      ...(filterLevelActions?.menuItems || [])
    ]

    return mergedMenuItems.length > 0 ? { menuItems: mergedMenuItems } : null
  }

  const { handleBulkAction, handleBulkModalAction } = useListBulkActions(config, endpoint, setModalType, onRefreshAggregate)

  const handleExpand = (item) => {
    console.log('handleExpand was clicked:', item)
  }

  const { showModal, setShowModal, modalConfig } = useFormModal()
  const { pushModal } = useModalStack()

  const getAllItems = useCallback(() => {
    const pages = result?.data?.pages || []
    return pages.flatMap(page => page?.data || [])
  }, [result?.data])

  const getVisibleIds = useCallback(() => {
    const items = getAllItems()
    return items.map(item => item.id)
  }, [getAllItems])

  const selection = useSelectionState({
    singleSelect: false,
    maxSelection: 100,
    getVisibleIds
  })

  useSelectionResetEffects({
    searchDeps: [debouncedSearchTerm, filterTerm],
    dataDeps: selectAction ? [] : [result?.dataUpdatedAt, result?.isSuccess, result?.data],
    clear: selection.clear
  })

  const initialSelectionDone = useRef(false)
  useEffect(() => {
    if (selectAction && result?.isSuccess && !initialSelectionDone.current) {
      const ids = getVisibleIds()
      if (ids.length > 0) {
        initialSelectionDone.current = true
        selection.selectAll()
      }
    }
  }, [result?.isSuccess, result?.data])

  const { onListAction: bulkListAction, onBulkModalCallback } = useBulkSelectionActions({
    getAllItems,
    getSelectedIds: () => selection.selectedItems,
    handleBulkAction,
    handleBulkModalAction,
    clear: selection.clear
  })

  // Declared before the export hook, which reads it for the on-screen-columns
  // profile. The effect that narrows it on resize stays further down.
  const [visibleColumns, setVisibleColumns] = useState(table?.columns)

  const { runExport, formats, profiles } = useListExport(endpoint, entity, visibleColumns, config?.listHeader?.text)
  const { planImport, applyImport, isPlanning, isApplying, importFormats, fieldLabel } = useListImport(endpoint, entity)

  /*
   * Export is intercepted ahead of the bulk path for two reasons: bulk actions
   * post once per selected row, and useBulkSelectionActions returns early when
   * the selection is empty — which for an export is the ordinary case, meaning
   * "all of it".
   */
  const onBulkListAction = useCallback(async (action) => {
    if (action?.action === 'import') {
      return pushModal(ImportModal, {
        isPlanning,
        isApplying,
        importFormats,
        fieldLabel,
        onPlan: ({ file }) => planImport({ file, useNaturalKey: true }),
        onApply: ({ file, approvals }) => applyImport({ file, approvals, useNaturalKey: true })
      })
    }

    if (action?.action !== 'export') return bulkListAction(action)

    pushModal(ExportModal, {
      formats,
      profiles,
      selectedCount: selection.selectedItems.size,
      onExport: ({ format, profile, scope }) => runExport({
        format,
        profile,
        scope,
        selectedIds: [...selection.selectedItems],
        // The query the list is running right now, minus pagination. Forwarding
        // it is what makes an "all" export honour the active search and filters.
        query: {
          orderBy: order?.[0],
          direction: order?.[1],
          ...(debouncedSearchTerm?.length >= 3 ? { searchString: debouncedSearchTerm } : {}),
          ...(filterTerm ? { filter: filterTerm } : {}),
          ...(serverFilter ? { serverFilter } : {}),
        }
      })
    })
  }, [bulkListAction, pushModal, formats, profiles, selection.selectedItems, runExport, planImport, applyImport, importFormats, fieldLabel, isPlanning, isApplying, order, debouncedSearchTerm, filterTerm, serverFilter])

  useEffect(() => {
    if (showModal === true) {
      const modalCallback = modalType === 'line' ? handleActions : onBulkModalCallback
      pushModal(PopUp, {
        config: modalConfig,
        setShow: setShowModal,
        setAction: modalCallback,
        data: {}
      })
      setShowModal(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, modalConfig, setShowModal, handleActions, onBulkModalCallback, modalType, pushModal])

  const sortList = useCallback((orderBy) => {
    setOrder(prevOrder => {
      if (!prevOrder) return [orderBy, 'asc']
      if (prevOrder[0] === orderBy) {
        return [orderBy, prevOrder[1] === 'asc' ? 'desc' : 'asc']
      }
      return [orderBy, 'asc']
    })
  }, [setOrder])

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth
      if (table?.dropOrder) {
        const reducedColumns = removeElementsByKey(table.columns, table.dropOrder, screenWidth)
        setVisibleColumns(reducedColumns)
      } else {
        setVisibleColumns(table.columns)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [table])

  return {
    getFilterActions,
    handleAction,
    handleExpand,
    handleBulkAction,
    selection,
    onBulkListAction,
    sortList,
    visibleColumns,
    getAllItems,
    moveItem,
    moveLoading,
    confirmMove,
    clearMoveItem,
  }
}

export default useViewTable
